import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { IntlProvider } from 'react-intl';
import {
  LOCALES,
  getSavedLocale,
  saveLocale,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from '@/i18n';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface LocaleContextValue {
  locale: SupportedLocale;
  setLocale: (next: SupportedLocale) => void;
  isLoading: boolean;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Centraliza o gerenciamento do idioma do usuário.
 *
 * Fontes (prioridade):
 *   1. profiles.preferred_locale (DB) — vale entre devices.
 *   2. localStorage — fonte rápida em runtime.
 *   3. DEFAULT_LOCALE ('pt-BR').
 *
 * Comportamento:
 *   - Carrega do localStorage no boot (síncrono).
 *   - Quando a sessão estiver pronta, sincroniza com profiles. Se diferir,
 *     adota o valor do DB e atualiza localStorage.
 *   - setLocale escreve nos dois lugares (localStorage + DB).
 *
 * Componentes da topbar (LocaleSwitcher) consomem via useLocale().
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [locale, setLocaleState] = useState<SupportedLocale>(() => getSavedLocale());
  const [synced, setSynced] = useState(false);

  // Sync com profiles.preferred_locale quando a sessão fica pronta.
  // Roda só uma vez por session pra evitar overrides em cada re-render.
  useEffect(() => {
    if (authLoading || synced || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('preferred_locale')
          .eq('user_id', user.id)
          .maybeSingle();
        if (cancelled) return;
        const dbLocale = (data as { preferred_locale?: string } | null)?.preferred_locale;
        if (dbLocale && dbLocale in LOCALES && dbLocale !== locale) {
          setLocaleState(dbLocale as SupportedLocale);
          saveLocale(dbLocale as SupportedLocale);
        }
      } catch {
        /* silencioso — mantém o que tá no localStorage */
      } finally {
        if (!cancelled) setSynced(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, synced, user, locale]);

  const setLocale = useCallback(
    (next: SupportedLocale) => {
      setLocaleState(next);
      saveLocale(next);
      // Persiste no DB em background (não bloqueia a UI).
      if (user) {
        supabase
          .from('profiles')
          .update({ preferred_locale: next })
          .eq('user_id', user.id)
          .then(({ error }) => {
            if (error) {
              console.error('[LocaleProvider] save preferred_locale', error);
            }
          });
      }
    },
    [user],
  );

  const messages = LOCALES[locale]?.messages ?? LOCALES[DEFAULT_LOCALE].messages;

  return (
    <LocaleContext.Provider
      value={{ locale, setLocale, isLoading: authLoading && !synced }}
    >
      <IntlProvider locale={locale} messages={messages} defaultLocale="pt-BR">
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale deve estar dentro de <LocaleProvider>');
  return ctx;
}
